import React from "react";
import { motion } from "framer-motion";
import MaskedReveal from "./MaskedReveal";
import BubblesBackground from "./BubblesBackground";
import { ArrowDown } from "lucide-react";

/**
 * PelageHero — a warm, editorial hero inspired by Pelage Pharma.
 * Cream canvas, three slow-drifting organic gradient orbs (crimson, rose, peach),
 * bubbles rising over the top, and a huge serif line-by-line reveal.
 * A small clipped "specimen" plant footage sits as an arch/circle inset —
 * the client's original plant footage stays visible but treated as an editorial
 * object rather than a fullscreen video.
 */

const VICTORY_LOGO =
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_75990839-45d2-4b14-a4ad-e51df4156482/artifacts/6zl932yo_Victory_Logo_Red.png";

const PLANT_STILL =
    "https://images.pexels.com/photos/38336747/pexels-photo-38336747.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600&h=800&fit=crop";

const PelageHero = () => {
    return (
        <section
            id="hero"
            className="relative isolate w-full overflow-hidden"
            style={{
                background:
                    "linear-gradient(180deg, #F6EFE1 0%, #F1E6D0 55%, #EDDCBB 100%)",
            }}
            data-testid="hero-section"
        >
            {/* Ambient organic gradient orbs */}
            <Orbs />

            {/* Warm grain overlay */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.4] mix-blend-multiply"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.55 0 0 0 0 0.35 0 0 0 0 0.15 0 0 0 0.22 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                }}
            />

            {/* Rising bubbles on the cream canvas */}
            <BubblesBackground variant="light" density={22} />

            {/* Nav */}
            <motion.header
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 1.6 }}
                className="relative z-30 mx-auto flex max-w-[1400px] items-center justify-between px-6 pt-6 md:px-12 md:pt-10"
            >
                <div className="flex items-center gap-3" data-testid="nav-brand">
                    <img
                        src={VICTORY_LOGO}
                        alt="Victory Foods and Beverages"
                        width="360"
                        height="240"
                        fetchPriority="high"
                        decoding="async"
                        className="h-16 w-auto md:h-24"
                    />
                </div>
                <div className="hidden items-center gap-8 text-xs uppercase tracking-[0.35em] text-crimson-800/80 md:flex">
                    <a
                        href="#bottle-reveal"
                        data-testid="nav-bottle"
                        className="group relative transition-colors hover:text-crimson-800"
                    >
                        · Bottle
                        <span className="absolute -bottom-1 left-2 h-px w-0 bg-crimson-800 transition-all duration-500 group-hover:w-[calc(100%-0.5rem)]" />
                    </a>
                    <a
                        href="#process"
                        data-testid="nav-process"
                        className="group relative transition-colors hover:text-crimson-800"
                    >
                        · Process
                        <span className="absolute -bottom-1 left-2 h-px w-0 bg-crimson-800 transition-all duration-500 group-hover:w-[calc(100%-0.5rem)]" />
                    </a>
                    <a
                        href="#notify"
                        data-testid="nav-notify"
                        className="group relative transition-colors hover:text-crimson-800"
                    >
                        · Notify
                        <span className="absolute -bottom-1 left-2 h-px w-0 bg-crimson-800 transition-all duration-500 group-hover:w-[calc(100%-0.5rem)]" />
                    </a>
                </div>
                <a
                    href="#notify"
                    data-testid="nav-cta"
                    className="hidden rounded-full border border-crimson-800/50 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-crimson-800 transition-colors hover:bg-crimson-800 hover:text-ivory-100 md:inline-flex"
                >
                    Get Notified
                </a>
            </motion.header>

            {/* Content grid */}
            <div className="relative z-20 mx-auto grid min-h-[calc(100vh-160px)] max-w-[1400px] grid-cols-12 gap-6 px-6 pb-16 pt-16 md:px-12 md:pb-24 md:pt-24">
                {/* Small caption top */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.9, duration: 0.9 }}
                    className="col-span-12 flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-crimson-800/70 md:text-xs"
                    data-testid="hero-tagline"
                >
                    <span className="inline-block h-px w-10 bg-crimson-800/50" />
                    Victory Foods and Beverages Private Limited · India
                </motion.div>

                {/* Big headline occupies left/main column */}
                <div className="col-span-12 md:col-span-9">
                    <MaskedReveal
                        as="h1"
                        className="font-serif-display mt-6 text-[16vw] leading-[0.92] tracking-tightest text-crimson-800 md:mt-8 md:text-[10vw]"
                        startDelay={2.05}
                        duration={1.2}
                        lines={[
                            "Launching",
                            <>
                                <em className="italic font-light">soon…</em>
                            </>,
                        ]}
                        data-testid="hero-headline"
                    />
                </div>

                {/* Right column: clipped plant specimen inset */}
                <div className="col-span-12 md:col-span-3 md:col-start-10 md:mt-8">
                    <PlantSpecimen />
                </div>

                {/* Bottom body + CTAs */}
                <div className="col-span-12 mt-16 grid grid-cols-12 items-end gap-6 md:mt-24">
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 3.0 }}
                        className="col-span-12 max-w-[42ch] text-sm leading-[1.7] text-ink-900/80 md:col-span-5 md:text-base"
                    >
                        Starting with Victory Cola — and more flavours on the
                        way, including our hot-fill juice range. Blown,
                        filled, labelled and sealed at our own plant.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 3.2 }}
                        className="col-span-12 flex flex-wrap items-center gap-4 md:col-span-6 md:col-start-7 md:justify-end"
                    >
                        <a
                            href="#notify"
                            data-testid="hero-cta-notify"
                            className="shine-btn brand-cursor inline-flex items-center gap-3 rounded-full bg-crimson-800 px-6 py-4 text-xs uppercase tracking-[0.35em] text-ivory-100 transition-transform hover:-translate-y-0.5"
                        >
                            Notify me at launch
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-ivory-100" />
                        </a>
                        <a
                            href="#bottle-reveal"
                            data-testid="hero-cta-process"
                            className="inline-flex items-center gap-3 rounded-full border border-crimson-800/50 px-6 py-4 text-xs uppercase tracking-[0.35em] text-crimson-800 transition-all hover:-translate-y-0.5 hover:border-crimson-800 hover:bg-crimson-800/[0.06]"
                        >
                            Meet the bottle
                            <ArrowDown size={12} />
                        </a>
                    </motion.div>
                </div>
            </div>

            {/* Bottom-right scroll indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 3.4, duration: 1 }}
                className="pointer-events-none absolute bottom-6 right-6 z-20 flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-crimson-800/70 md:right-12"
            >
                <span className="h-px w-8 bg-crimson-800/50" />
                Scroll
            </motion.div>

            {/* Gold hairline divider at the bottom */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-px">
                <div className="divider-gold h-full w-full" />
            </div>
        </section>
    );
};

/**
 * Two ambient organic gradient orbs. Lower blur radius for perf on production.
 * Framer animate transforms only — no size/blur transitions.
 */
const Orbs = () => {
    const orbs = [
        {
            id: 1,
            size: 780,
            color1: "#B91C1C",
            color2: "rgba(185,28,28,0)",
            top: "-10%",
            left: "-15%",
            blur: 90,
            duration: 26,
            drift: 60,
        },
        {
            id: 2,
            size: 640,
            color1: "#E38B7D",
            color2: "rgba(227,139,125,0)",
            top: "20%",
            left: "58%",
            blur: 90,
            duration: 30,
            drift: -70,
        },
    ];
    return (
        <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
        >
            {orbs.map((o) => (
                <motion.div
                    key={o.id}
                    className="absolute rounded-full"
                    style={{
                        width: o.size,
                        height: o.size,
                        top: o.top,
                        left: o.left,
                        filter: `blur(${o.blur}px)`,
                        background: `radial-gradient(circle at 50% 50%, ${o.color1} 0%, ${o.color2} 65%)`,
                        opacity: 0.45,
                        willChange: "transform",
                    }}
                    animate={{
                        x: [0, o.drift, 0],
                        y: [0, -20, 0],
                    }}
                    transition={{
                        duration: o.duration,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />
            ))}
        </div>
    );
};

/**
 * A small "specimen" of the plant footage — treated as an editorial arch.
 * Keeps the client's original plant imagery in the hero without dominating it.
 */
const PlantSpecimen = () => (
    <motion.figure
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 2.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative ml-auto w-full max-w-[280px]"
        data-testid="hero-plant-specimen"
    >
        <div
            className="relative aspect-[3/4] w-full overflow-hidden bg-crimson-800"
            style={{
                borderTopLeftRadius: "9999px",
                borderTopRightRadius: "9999px",
            }}
        >
            {/* Static plant still — no infinite Ken-burns loop for perf */}
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: `url(${PLANT_STILL})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "saturate(0.9) contrast(1.05)",
                }}
            />
            {/* Crimson wash for editorial cohesion */}
            <div className="absolute inset-0 bg-crimson-800/35 mix-blend-multiply" />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(to top, rgba(139,0,0,0.75) 0%, rgba(0,0,0,0) 60%)",
                }}
            />
            {/* Corner marks */}
            <div className="absolute left-3 top-3 h-3 w-3 border-l border-t border-ivory-100/70" />
            <div className="absolute right-3 top-3 h-3 w-3 rotate-90 border-l border-t border-ivory-100/70" />
        </div>
        <figcaption className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.35em] text-crimson-800/70">
            <span>Fig. 01 — Plant</span>
            <span>Live</span>
        </figcaption>
    </motion.figure>
);

export default PelageHero;
